<script setup lang="ts">
import { computed } from 'vue'
import type { UseCaseArticleRecipe } from '@/data/useCases'
import ArticlePrompt from './ArticlePrompt.vue'
import ArticleVideo from './ArticleVideo.vue'
import ArticleZoom from './ArticleZoom.vue'
import { splitLabel } from '@/composables/articleLabel'

const props = defineProps<{ recipe: UseCaseArticleRecipe; index?: number; total?: number }>()

// A clip cut from the film has no output: its input stands in at full size, with the note on it.
const cut = computed(() => !props.recipe.output && !!props.recipe.inputs?.length)
const hasText = computed(() => !!((props.recipe.note && !cut.value) || props.recipe.focus))
const label = computed(() => splitLabel(props.recipe.label))
</script>

<template>
  <!-- One thing made, read left to right: what went in, what came out, then the
       words that did it. The header names the card; a skimmer gets the picture
       and the one sentence that is different about this shot, and the full prompt
       is one tap away. Below sm the input row sits above the output so the two are
       never confused; every picture opens the lightbox. -->
  <div class="rounded-card border border-surface-800 bg-surface-900 p-3 sm:p-4">
    <p class="flex items-baseline gap-2">
      <span v-if="total && total > 1 && index" class="shrink-0 text-xs text-surface-400">{{ index }} of {{ total }}</span>
      <span class="text-base font-semibold text-surface-50">{{ label.lead }}<span v-if="label.rest" class="font-normal text-surface-400"> · {{ label.rest }}</span></span>
    </p>

    <div class="mt-3 grid gap-3" :class="hasText ? 'sm:grid-cols-[minmax(0,11fr)_minmax(0,9fr)] sm:gap-5 sm:items-start' : ''">
      <div class="sm:flex sm:items-center sm:gap-3">
        <figure v-if="cut" class="relative w-full min-w-0 sm:flex-1">
          <ArticleZoom :item="{ kind: 'recipe', recipe }" :label="recipe.label">
            <img :src="recipe.inputs![0].src" alt="" loading="lazy" class="block w-full h-auto rounded border border-surface-700 bg-black" />
          </ArticleZoom>
          <span v-if="recipe.note" class="pointer-events-none absolute left-2 top-2 rounded-full bg-surface-950/85 px-3 py-1 text-sm font-medium text-surface-50">{{ recipe.note }}</span>
        </figure>
        <div v-else-if="recipe.inputs" class="mb-2 flex gap-3 sm:mb-0 sm:flex-col sm:gap-1.5 sm:shrink-0">
          <template v-if="recipe.inputs.length">
            <figure v-for="input in recipe.inputs" :key="input.src" class="w-24">
              <ArticleZoom :item="{ kind: 'image', src: input.src, alt: input.label, caption: input.label }" :label="input.label" small>
                <img :src="input.src" alt="" loading="lazy" class="block w-full aspect-video object-cover rounded border border-surface-600 bg-black" />
              </ArticleZoom>
              <figcaption class="mt-1 text-xs leading-tight text-surface-400">{{ input.label }}</figcaption>
            </figure>
          </template>
          <p v-else class="w-24 text-xs leading-tight text-surface-400">No reference picture</p>
        </div>

        <template v-if="recipe.output">
          <svg v-if="recipe.inputs" class="hidden sm:block shrink-0 w-6 h-6 text-primary-300" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10h13M11 5l5 5-5 5" /></svg>
          <figure class="w-full min-w-0 sm:flex-1">
            <div class="relative rounded overflow-hidden border border-surface-700 bg-black">
              <ArticleZoom v-if="recipe.output.kind === 'image'" :item="{ kind: 'recipe', recipe }" :label="recipe.output.alt">
                <img :src="recipe.output.src" :alt="recipe.output.alt" loading="lazy" class="block w-full h-auto" />
              </ArticleZoom>
              <template v-else>
                <ArticleVideo
                  v-if="recipe.output.kind === 'video'"
                  :src="recipe.output.src"
                  :poster="recipe.output.poster"
                  :alt="recipe.output.alt"
                  :sound="recipe.output.sound"
                />
                <div v-else class="bg-surface-800">
                  <img v-if="recipe.output.poster" :src="recipe.output.poster" :alt="recipe.output.alt" loading="lazy" class="block w-full h-auto" />
                  <audio :src="recipe.output.src" :aria-label="recipe.output.alt" controls preload="none" class="block w-full"></audio>
                </div>
                <ArticleZoom v-if="recipe.output.kind === 'video'" badge-only :item="{ kind: 'recipe', recipe }" :label="recipe.label" />
              </template>
            </div>
            <figcaption v-if="recipe.outputLabel" class="mt-1 text-xs text-surface-400">{{ recipe.outputLabel }}</figcaption>
          </figure>
        </template>
      </div>

      <div v-if="hasText" class="min-w-0">
        <p v-if="recipe.note && !cut" class="text-sm text-surface-300 leading-relaxed">{{ recipe.note }}</p>
        <blockquote v-if="recipe.focus" class="border-l-2 border-primary-300 pl-3 text-sm sm:text-base text-surface-100 leading-relaxed" :class="recipe.note ? 'mt-3' : ''">{{ recipe.focus }}</blockquote>
      </div>
    </div>

    <details v-if="recipe.prompt" name="article-recipe-prompt" class="group mt-2">
      <summary class="cursor-pointer inline-flex items-center gap-1.5 min-h-11 text-sm text-primary-300">
        Full prompt
        <svg class="w-4 h-4 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l5 5 5-5" /></svg>
      </summary>
      <ArticlePrompt class="mt-1" compact :aria-name="`${recipe.label} prompt`" :text="recipe.prompt" :focus="recipe.focus" />
    </details>
  </div>
</template>
